import React, { useCallback } from "react";
import { Divider, Grid } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import type { Customer } from "@/types/customer-type";
import { type CustomerFormValues } from "@/api/types";
import { useToast } from "@/hooks/use-toast";
import { CustomerBasicInfoSection } from "./customer-basic-info-section";
import { CustomerPreferencesSection } from "./customer-preferences-section";

type CustomerFormMode = "create" | "update";

interface CustomerFormProps {
  form: UseFormReturn<CustomerFormValues>;
  mode: CustomerFormMode;
  onSubmit?: (
    values: CustomerFormValues,
    context: { mode: CustomerFormMode }
  ) => Promise<Customer | { data: Customer } | void>;
  onSuccess?: (saved: Customer) => void;
  onCancel?: () => void;
}

export function CustomerForm({
  form,
  mode,
  onSubmit,
  onSuccess,
}: CustomerFormProps) {
  const { toast } = useToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleFormSubmit = useCallback(
    async (values: CustomerFormValues) => {
      if (!onSubmit) {
        return;
      }
      try {
        const result = await onSubmit(values, { mode });

        const saved =
          (result as { data?: Customer })?.data ??
          (result as Customer | undefined);

        if (saved) {
          toast({
            title: mode === "create" ? "Customer created" : "Customer updated",
          });
          onSuccess?.(saved);
        }

        return saved;
      } catch (error: any) {
        toast({
          title: "Operation failed",
          description: error?.message ?? "Please try again.",
          variant: "destructive",
        });
        throw error;
      }
    },
    [mode, onSubmit, onSuccess, toast]
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <CustomerBasicInfoSection register={register} errors={errors} />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomerPreferencesSection control={control} errors={errors} />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2, border: 0 }} />
    </form>
  );
}

export default CustomerForm;
